import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { GraduationCap, CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import api from '../lib/api';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error, already_verified
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  
  // Prevent duplicate API calls (React StrictMode double-render protection)
  const hasVerified = useRef(false);

  useEffect(() => {
    // Skip if already verified or no token
    if (hasVerified.current || !token) {
      if (!token) {
        setStatus('error');
        setMessage('No verification token provided');
      }
      return;
    }
    
    // Mark as verified attempt started
    hasVerified.current = true;
    
    const verifyEmail = async () => {
      try {
        const response = await api.get(`/auth/verify-email?token=${token}`);
        
        if (response.data.already_verified) {
          setStatus('already_verified');
          setMessage('Your email has already been verified.');
        } else {
          setStatus('success');
          setMessage('Your email has been verified successfully!');
          setEmail(response.data.email || '');
        }
      } catch (error) {
        const errorDetail = error.response?.data?.detail || '';
        
        // Check if this might be a "token already used" scenario
        // by seeing if user is actually verified despite the error
        if (errorDetail.toLowerCase().includes('invalid')) {
          // Token was likely already consumed - check auth status
          try {
            const authToken = localStorage.getItem('token');
            if (authToken) {
              const meResponse = await api.get('/auth/me');
              if (meResponse.data.email_verified) {
                setStatus('already_verified');
                setMessage('Your email has already been verified.');
                return;
              }
            }
          } catch (authError) {
            // Ignore auth check errors
          }
        }
        
        setStatus('error');
        setMessage(errorDetail || 'Failed to verify email. The link may be invalid or expired.');
      }
    };
    
    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-12" data-testid="verify-email-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <GraduationCap className="h-10 w-10 text-indigo-700" />
            <span className="font-heading text-2xl font-semibold text-slate-900">
              Gifted Academy Tutors
            </span>
          </Link>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Email Verification</h1>
        </div>

        <Card className="shadow-card border-stone-100">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-xl text-center">
              {status === 'verifying' && 'Verifying...'}
              {status === 'success' && 'Email Verified!'}
              {status === 'already_verified' && 'Already Verified'}
              {status === 'error' && 'Verification Failed'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center py-6">
            {status === 'verifying' && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
                </div>
                <p className="text-slate-600">Please wait while we verify your email...</p>
              </div>
            )}

            {status === 'success' && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-slate-900 font-medium">{message}</p>
                {email && (
                  <p className="text-sm text-slate-600">
                    Verified email: <span className="font-medium">{email}</span>
                  </p>
                )}
                <p className="text-sm text-slate-500">
                  You can now access all features of your account.
                </p>
                <Link to="/login">
                  <Button className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800">
                    Continue to Login
                  </Button>
                </Link>
              </div>
            )}

            {status === 'already_verified' && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-blue-600" />
                </div>
                <p className="text-slate-900 font-medium">{message}</p>
                <p className="text-sm text-slate-500">
                  Your email was already verified. You can log in to your account.
                </p>
                <Link to="/login">
                  <Button className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800">
                    Go to Login
                  </Button>
                </Link>
              </div>
            )}

            {status === 'error' && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
                <p className="text-slate-900 font-medium">{message}</p>
                <p className="text-sm text-slate-500">
                  You can request a new verification email from your dashboard or login page.
                </p>
                <div className="flex gap-3 mt-4">
                  <Link to="/login">
                    <Button variant="outline" className="rounded-full">
                      Go to Login
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
